// IndicatorModal "Kırılım" bölümü (spec §6.3, §18 DR3/DR6; Modal.dc.html sağ panel). Toplam
// satırında: "Toplam nereden hesaplanır" radyosu (Otomatik — şu an X / uygun gruplar / pasif +
// gerekçe; formülde `TOPLA_ALT("d")` varsa kilitli "formül belirliyor"), grup listesi (rol eki +
// üyeler · tam / seçili kırılımlar), "Katalogda düzenle" / "+ Kırılım grubu ekle", birim notu.
// Seçim set ağacı taslağına yazılır (`calculationGroupMetricId`; kaydet çubuğu PATCH'i).

import type { KpiSetIndicator } from "@mahir/shared";
import { KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import { calcGroupState, groupRole, type SetBreakdownView } from "../../lib/kpi-breakdown-set";
import { btnGhost, codeStyle, labelStyle } from "./kpi-form-styles";

const C = KPI_BREAKDOWN_COPY.calcGroup;
const help = { fontSize: "0.875rem", color: "var(--color-text-3)" } as const;
const secondary = {
  ...btnGhost,
  minHeight: 44,
  color: "var(--color-primary)",
  borderColor: "var(--color-primary)",
  fontWeight: 600,
} as const;

export interface IndicatorBreakdownSectionProps {
  indicator: KpiSetIndicator;
  view: SetBreakdownView<KpiSetIndicator>;
  disabled: boolean;
  onChange: (calculationGroupMetricId: string | null) => void;
  onEditCatalog?: (() => void) | undefined;
  onAddGroup?: (() => void) | undefined;
}

export function IndicatorBreakdownSection(props: IndicatorBreakdownSectionProps): JSX.Element {
  const { indicator: total, view, disabled } = props;
  const state = calcGroupState(total, view);
  const groups = view.groupsOf(total.id);
  const locked = state.lock !== null;
  // açık seçim artık uygun değilse (seçili kırılım / hesaplama yok / üyesiz) Otomatik işaretli görünür
  const explicitOk = state.options.some(
    (o) => o.metricId === state.explicitId && o.disabledReason === null,
  );
  const checkedId = state.lock
    ? (state.lock.group?.metricId ?? null)
    : explicitOk
      ? state.explicitId
      : null;
  const firstDim = groups[0]?.breakdown?.dimension ?? "cinsiyet";
  const radio = (
    id: string | null,
    label: JSX.Element | string,
    reason: string | null,
  ): JSX.Element => {
    const off = disabled || locked || reason !== null;
    return (
      <label
        key={id ?? "auto"}
        className="flex items-start gap-2"
        style={{ padding: "6px 0", color: reason ? "var(--color-text-3)" : "var(--color-text)" }}
      >
        <input
          type="radio"
          name={`calc-group-${total.id}`}
          value={id ?? ""}
          checked={checkedId === id}
          disabled={off}
          style={{ marginTop: 4, width: 18, height: 18 }}
          onChange={() => props.onChange(id)}
        />
        <span>
          {label}
          {reason ? <span style={help}> — {reason}</span> : null}
        </span>
      </label>
    );
  };

  return (
    <section
      aria-labelledby={`bd-section-${total.id}`}
      data-testid="kpi-indicator-breakdown"
      className="rounded-lg p-3 grid gap-3"
      style={{ border: "1px solid var(--color-border)", marginBottom: 12 }}
    >
      <div>
        <h3 id={`bd-section-${total.id}`} style={{ margin: 0, fontSize: "1rem", fontWeight: 700 }}>
          {C.kicker}
        </h3>
        <div style={help}>{C.summary(total.labelOverride ?? total.label, groups.length)}</div>
      </div>
      <fieldset
        style={{ border: 0, padding: 0, margin: 0 }}
        aria-describedby={`bd-lock-${total.id}`}
      >
        <legend style={labelStyle}>
          {C.legend}
          {locked ? <span style={{ ...help, textTransform: "none" }}> · {C.locked}</span> : null}
        </legend>
        {radio(
          null,
          <>
            {C.auto} <strong>{state.auto?.label ?? C.autoNone}</strong>
          </>,
          null,
        )}
        {state.options.map((o) => radio(o.metricId, o.label, o.disabledReason))}
      </fieldset>
      <div
        id={`bd-lock-${total.id}`}
        data-testid="kpi-calc-group-lock"
        className="rounded-md"
        style={{ ...help, border: "1px solid var(--color-border)", padding: "8px 12px" }}
      >
        {C.lockLead} <code style={codeStyle}>{state.lock?.call ?? C.exampleCall(firstDim)}</code>{" "}
        {state.lock ? C.lockActive : C.lockIf} <em>{C.locked}</em>.
      </div>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, fontSize: "0.875rem" }}>
        {groups.map((g) => {
          const role = groupRole(total, g, view);
          const members = view.membersOf(g.id).map((m) => m.labelOverride ?? m.label);
          return (
            <li key={g.id} className="flex justify-between gap-3" style={{ padding: "3px 0" }}>
              <span>
                <strong>{g.label}</strong>
                {role === "calc"
                  ? ` ${KPI_BREAKDOWN_COPY.calcGroupSuffix}`
                  : role === "check"
                    ? ` ${C.roleCheck}`
                    : ""}
              </span>
              <span style={{ ...help, textAlign: "right" }}>
                {C.membersLine(members.join(", "), role !== "partial")}
              </span>
            </li>
          );
        })}
      </ul>
      <div className="flex gap-2 flex-wrap">
        {props.onEditCatalog ? (
          <button type="button" style={secondary} onClick={props.onEditCatalog}>
            {C.editInCatalog}
          </button>
        ) : null}
        {props.onAddGroup ? (
          <button type="button" style={secondary} disabled={disabled} onClick={props.onAddGroup}>
            {C.addGroup}
          </button>
        ) : null}
      </div>
      <div style={{ ...help, borderTop: "1px solid var(--color-border)", paddingTop: 10 }}>
        {C.footerLead} <strong>{C.footerInherit(total.unit ?? "")}</strong>
        {C.footerTail}
      </div>
    </section>
  );
}
